import React, { useState } from "react";
import { ChevronDown, ChevronUp, Eye, EyeOff } from "lucide-react";
import { supabase } from "../../../supabaseClient";
import { useToast } from "../../../hooks/useToast";
import { friendlyAuthError } from "../../../utils/friendlyAuthError";

function buildSummary(form) {
  const parts = [];
  if (form.trade) parts.push(`a ${form.trade.toLowerCase()} quote`);
  if (form.projectType) parts.push(`for ${form.projectType.toLowerCase()}`);
  if (form.suburb) parts.push(`in ${form.suburb}`);
  if (form.propertyType) parts.push(`(${form.propertyType.toLowerCase()})`);
  return `You're requesting ${parts.join(" ")}.`;
}

function buildTradeDetails(form) {
  const tsa = form.tradeSpecificAnswers || {};
  const items = [];

  switch (form.trade) {
    case "Painting":
      if (tsa.paintWhat) items.push({ label: "What needs painting", value: tsa.paintWhat });
      if (tsa.paintRooms) items.push({ label: "Rooms", value: tsa.paintRooms });
      if (tsa.paintCondition) items.push({ label: "Condition", value: tsa.paintCondition });
      if (tsa.paintSupply) items.push({ label: "Paint supply", value: tsa.paintSupply });
      if (tsa.paintNotes) items.push({ label: "Notes", value: tsa.paintNotes });
      break;
    case "Plumbing":
      if (tsa.plumbUrgency) items.push({ label: "Urgency", value: tsa.plumbUrgency });
      if (tsa.plumbArea) items.push({ label: "Area", value: tsa.plumbArea });
      if (tsa.plumbAge) items.push({ label: "Property age", value: tsa.plumbAge });
      if (tsa.plumbNotes) items.push({ label: "Description", value: tsa.plumbNotes });
      break;
    case "Electrical":
      if (tsa.elecSafety) items.push({ label: "Safety issue", value: tsa.elecSafety });
      if (tsa.elecArea) items.push({ label: "Area", value: tsa.elecArea });
      if (tsa.elecBoard) items.push({ label: "Switchboard age", value: tsa.elecBoard });
      if (tsa.elecNotes) items.push({ label: "Notes", value: tsa.elecNotes });
      break;
    case "Solar & Battery":
      if (tsa.solarRoof) items.push({ label: "Roof type", value: tsa.solarRoof });
      if (tsa.solarExisting) items.push({ label: "Existing system", value: tsa.solarExisting });
      if (tsa.solarSize) items.push({ label: "System size", value: tsa.solarSize });
      if (tsa.solarNotes) items.push({ label: "Notes", value: tsa.solarNotes });
      break;
    case "Building":
      if (tsa.buildStage) items.push({ label: "Stage", value: tsa.buildStage });
      if (tsa.buildSize) items.push({ label: "Project size", value: tsa.buildSize });
      if (tsa.buildApproval) items.push({ label: "Council approval", value: tsa.buildApproval });
      if (tsa.buildNotes) items.push({ label: "Description", value: tsa.buildNotes });
      break;
    case "Carpentry":
      if (tsa.carpType) items.push({ label: "Type", value: tsa.carpType });
      if (tsa.carpSupply) items.push({ label: "Materials", value: tsa.carpSupply });
      if (tsa.carpNotes) items.push({ label: "Description", value: tsa.carpNotes });
      break;
    case "Concreter":
      if (tsa.concArea) items.push({ label: "Area", value: tsa.concArea });
      if (tsa.concFinish) items.push({ label: "Finish", value: tsa.concFinish });
      if (tsa.concExcavate) items.push({ label: "Excavation needed", value: tsa.concExcavate });
      if (tsa.concNotes) items.push({ label: "Notes", value: tsa.concNotes });
      break;
    case "HVAC":
      if (tsa.hvacProperty) items.push({ label: "Property type", value: tsa.hvacProperty });
      if (tsa.hvacRooms) items.push({ label: "Rooms", value: tsa.hvacRooms });
      if (tsa.hvacExisting) items.push({ label: "Existing system", value: tsa.hvacExisting });
      if (tsa.hvacNotes) items.push({ label: "Notes", value: tsa.hvacNotes });
      break;
    case "Landscaping":
      if (tsa.landSize) items.push({ label: "Yard size", value: tsa.landSize });
      if (tsa.landSlope) items.push({ label: "Slope", value: tsa.landSlope });
      if (tsa.landAccess) items.push({ label: "Access", value: tsa.landAccess });
      if (tsa.landNotes) items.push({ label: "Notes", value: tsa.landNotes });
      break;
    case "Roofing":
      if (tsa.roofStoreys) items.push({ label: "Storeys", value: tsa.roofStoreys });
      if (tsa.roofType) items.push({ label: "Roof type", value: tsa.roofType });
      if (tsa.roofInspect) items.push({ label: "Last inspected", value: tsa.roofInspect });
      if (tsa.roofNotes) items.push({ label: "Issue", value: tsa.roofNotes });
      break;
    case "Cleaning":
      if (tsa.cleanBeds) items.push({ label: "Bedrooms", value: tsa.cleanBeds });
      if (tsa.cleanBaths) items.push({ label: "Bathrooms", value: tsa.cleanBaths });
      if (tsa.cleanExtras && tsa.cleanExtras.length > 0) items.push({ label: "Extras", value: tsa.cleanExtras.join(", ") });
      if (tsa.cleanLast) items.push({ label: "Last cleaned", value: tsa.cleanLast });
      break;
    case "Handyman":
      if (tsa.handyCount) items.push({ label: "Tasks", value: tsa.handyCount });
      if (tsa.handySupply) items.push({ label: "Materials", value: tsa.handySupply });
      if (tsa.handyNotes) items.push({ label: "What needs doing", value: tsa.handyNotes });
      break;
    default:
      break;
  }

  return items;
}

// Detect "this email is already registered" across GoTrue versions.
function isExistingAccountError(err) {
  if (!err) return false;
  const msg = (err.message || "").toLowerCase();
  return (
    msg.includes("already registered") ||
    msg.includes("already exists") ||
    msg.includes("user already") ||
    err.status === 422
  );
}

/**
 * StepReview — final step of the lead-submit flow.
 *
 * 2026-04-30 rebuild: account creation is now OPTIONAL. The primary CTA
 * always submits the request anonymously; an expandable section below the
 * button lets the customer create an account (or sign in) so they can
 * track + chat. This was a P0 fix per Shannon — forcing signup was
 * costing leads.
 */
export default function StepReview({
  form,
  onSubmit,
  submitting,
  session,
}) {
  const { showToast } = useToast();

  // "anonymous" (default — no account, just submit) | "signup" | "signin"
  const [mode, setMode] = useState("anonymous");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [signInPassword, setSignInPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSignInPassword, setShowSignInPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const summary = buildSummary(form);
  const tradeDetails = buildTradeDetails(form);

  // Anonymous (or already-signed-in) submit — no auth work, just send.
  function handleAnonymousSubmit() {
    onSubmit();
  }

  async function handleSignupAndSubmit(e) {
    e?.preventDefault?.();

    const email = (form.email || "").trim();
    if (!email) {
      showToast("Please go back and add your email so we can create your account.", "error");
      return;
    }
    if (!password || !confirmPassword) {
      showToast("Please set a password to create your account.", "error");
      return;
    }
    if (password.length < 6) {
      showToast("Password must be at least 6 characters.", "error");
      return;
    }
    if (password !== confirmPassword) {
      showToast("Passwords don't match.", "error");
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) {
        if (isExistingAccountError(error)) {
          showToast("You already have an account. Sign in to submit.", "info");
          setMode("signin");
          return;
        }
        throw error;
      }

      const userId = data.user?.id;
      if (!userId) throw new Error("Sign up succeeded but no user ID returned.");

      // Don't overwrite an existing non-customer profile — see prior incident
      // where tradie accounts got silently demoted to customers.
      const { data: existingProfile } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", userId)
        .maybeSingle();

      if (existingProfile && existingProfile.role && existingProfile.role !== "customer") {
        showToast("This email already has an account. Sign in to submit.", "info");
        await supabase.auth.signOut();
        setMode("signin");
        return;
      }

      if (!data.session) {
        showToast(
          "Account created. Check your email to confirm, then sign in to submit your request.",
          "info"
        );
        return;
      }

      const { error: rpcError } = await supabase.rpc("create_customer_account", {
        p_user_id: userId,
        p_full_name: form.fullName?.trim() || "",
      });
      if (rpcError) {
        const { error: insertError } = await supabase
          .from("profiles")
          .insert({ id: userId, full_name: form.fullName?.trim() || "", role: "customer" });
        if (insertError) {
          await supabase.auth.signOut();
          throw new Error("Account setup failed. Please try again.");
        }
      }

      await supabase.auth.refreshSession();
      onSubmit({ freshCustomerUserId: userId });
    } catch (err) {
      showToast(friendlyAuthError(err.message) || "Sign up failed.", "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleSignInAndSubmit(e) {
    e?.preventDefault?.();

    const email = (form.email || "").trim();
    if (!email || !signInPassword) {
      showToast("Please enter your password to sign in.", "error");
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password: signInPassword,
      });
      if (error) throw error;

      const userId = data.session?.user?.id;
      if (!userId) throw new Error("Sign in returned no session.");

      onSubmit({ freshCustomerUserId: userId });
    } catch (err) {
      showToast(friendlyAuthError(err.message) || "Sign in failed.", "error");
    } finally {
      setLoading(false);
    }
  }

  const tradeForConsent = form.trade || "the tradie";

  return (
    <div>
      {/* ── Summary card ─────────────────────────────────────────── */}
      <div
        style={{
          background: "rgba(255, 138, 0,0.05)",
          borderLeft: "3px solid #FF8A00",
          padding: "16px",
          borderRadius: "8px",
          marginBottom: "20px",
        }}
      >
        <p style={{ margin: 0, fontWeight: 600, fontSize: "1rem", color: "var(--text, #1C1C1E)" }}>{summary}</p>
        <div style={{ marginTop: "12px", fontSize: "0.88rem", color: "var(--muted, #6b7280)", display: "flex", flexDirection: "column", gap: "4px" }}>
          {form.fullName && <span><strong>Name:</strong> {form.fullName}</span>}
          {form.phone && <span><strong>Phone:</strong> {form.phone}</span>}
          {form.email && <span><strong>Email:</strong> {form.email}</span>}
          {form.address && <span><strong>Address:</strong> {form.address}{form.suburb ? `, ${form.suburb}` : ""}</span>}
          {!form.address && form.suburb && <span><strong>Suburb:</strong> {form.suburb}</span>}
          {form.budgetRange && <span><strong>Budget:</strong> {form.budgetRange}</span>}
          {form.whenNeeded && <span><strong>Timing:</strong> {form.whenNeeded}</span>}
          {form.uploadedPhotos && form.uploadedPhotos.length > 0 && <span><strong>Photos:</strong> {form.uploadedPhotos.length} attached</span>}
        </div>
        {tradeDetails.length > 0 && (
          <div style={{ marginTop: "10px", paddingTop: "10px", borderTop: "1px solid rgba(255, 138, 0,0.15)", fontSize: "0.88rem", color: "var(--muted, #6b7280)", display: "flex", flexDirection: "column", gap: "4px" }}>
            {tradeDetails.map(({ label, value }) => (
              <span key={label}><strong>{label}:</strong> {value}</span>
            ))}
          </div>
        )}
      </div>

      {/* ── No-contact warning — keep, it's still useful ──────────── */}
      {!form.phone && !form.email && (
        <div
          style={{
            background: "#fffbeb",
            border: "1px solid #f59e0b",
            borderRadius: "12px",
            padding: "12px 14px",
            fontSize: "0.875rem",
            color: "#92400e",
            marginBottom: "16px",
          }}
        >
          ⚠️ You haven't added a phone number or email. The tradie won't be able to contact you.
        </div>
      )}

      {/* ── Consent line above primary CTA ────────────────────────── */}
      <p
        style={{
          margin: "0 0 12px",
          fontSize: "0.82rem",
          color: "var(--muted, #6b7280)",
          lineHeight: 1.5,
          textAlign: "center",
        }}
      >
        By submitting, you agree to share these details with this tradie so they
        can prepare a {tradeForConsent.toLowerCase()} quote for you.
      </p>

      {/* ── Primary CTA: anonymous submit (or already-signed-in) ─── */}
      {(session || mode === "anonymous") && (
        <button
          type="button"
          onClick={handleAnonymousSubmit}
          disabled={submitting || loading}
          style={{
            background: "var(--primary)",
            color: "#fff",
            border: "none",
            borderRadius: "14px",
            padding: "18px 32px",
            fontSize: "1.05rem",
            fontWeight: 700,
            cursor: (submitting || loading) ? "not-allowed" : "pointer",
            width: "100%",
            opacity: (submitting || loading) ? 0.7 : 1,
          }}
        >
          {submitting ? "Sending…" : "Send my quote request →"}
        </button>
      )}

      {/* ── Optional account creation — collapsed by default ────── */}
      {!session && mode === "anonymous" && (
        <button
          type="button"
          onClick={() => setMode("signup")}
          style={{
            marginTop: "16px",
            background: "transparent",
            color: "var(--text, #1C1C1E)",
            border: "1px dashed var(--border, rgba(140,100,80,0.28))",
            borderRadius: "12px",
            padding: "14px 16px",
            fontSize: "0.9rem",
            fontWeight: 600,
            cursor: "pointer",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          <ChevronDown size={16} />
          Create an account to track this quote &amp; chat with the tradie
        </button>
      )}

      {/* ── Signup form (expanded) ─────────────────────────────── */}
      {!session && mode === "signup" && (
        <div
          style={{
            marginTop: "16px",
            padding: "20px",
            borderRadius: "12px",
            border: "1px solid var(--border, #e5e7eb)",
            background: "var(--surface, #fff)",
          }}
        >
          <button
            type="button"
            onClick={() => setMode("anonymous")}
            style={{
              background: "none",
              border: "none",
              color: "var(--muted, #6b7280)",
              fontSize: "0.82rem",
              cursor: "pointer",
              padding: 0,
              marginBottom: "12px",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <ChevronUp size={14} /> Skip — just send my request
          </button>

          <p style={{ margin: "0 0 6px", fontWeight: 700, fontSize: "1rem", color: "var(--text, #1C1C1E)" }}>
            Create your account
          </p>
          <p style={{ margin: "0 0 16px", fontSize: "0.86rem", color: "var(--muted, #6b7280)", lineHeight: 1.5 }}>
            We'll send quote notifications and let you track + chat from your dashboard. Using <strong style={{ color: "var(--text, #1C1C1E)" }}>{form.email || "(no email — go back and add one)"}</strong>.
          </p>

          <form onSubmit={handleSignupAndSubmit}>
            <div style={{ marginBottom: "12px" }}>
              <label style={{ display: "block", fontWeight: 600, marginBottom: "6px", fontSize: "0.9rem" }}>Password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  style={{ width: "100%", fontSize: "1rem", padding: "14px 46px 14px 14px", borderRadius: "12px", border: "2px solid var(--border, #e5e7eb)", boxSizing: "border-box" }}
                  disabled={loading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--muted, #6b7280)", display: "flex", alignItems: "center" }}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: "6px" }}>
              <label style={{ display: "block", fontWeight: 600, marginBottom: "6px", fontSize: "0.9rem" }}>Confirm password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  style={{ width: "100%", fontSize: "1rem", padding: "14px 46px 14px 14px", borderRadius: "12px", border: "2px solid var(--border, #e5e7eb)", boxSizing: "border-box" }}
                  disabled={loading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--muted, #6b7280)", display: "flex", alignItems: "center" }}
                  tabIndex={-1}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || submitting}
              style={{
                marginTop: "16px",
                background: "var(--primary)",
                color: "#fff",
                border: "none",
                borderRadius: "14px",
                padding: "16px 32px",
                fontSize: "1rem",
                fontWeight: 700,
                cursor: (loading || submitting) ? "not-allowed" : "pointer",
                width: "100%",
                opacity: (loading || submitting) ? 0.7 : 1,
              }}
            >
              {loading || submitting ? "Creating account & sending…" : "Create account & send →"}
            </button>

            <div style={{ marginTop: "14px", paddingTop: "12px", borderTop: "1px solid var(--border, #e5e7eb)", textAlign: "center", fontSize: "0.85rem", color: "var(--muted, #6b7280)" }}>
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => setMode("signin")}
                style={{ background: "none", border: "none", padding: 0, color: "var(--primary)", fontWeight: 600, cursor: "pointer", textDecoration: "underline", fontSize: "inherit" }}
              >
                Sign in to submit
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Signin form (expanded) ─────────────────────────────── */}
      {!session && mode === "signin" && (
        <div
          style={{
            marginTop: "16px",
            padding: "20px",
            borderRadius: "12px",
            border: "1px solid var(--border, #e5e7eb)",
            background: "var(--surface, #fff)",
          }}
        >
          <button
            type="button"
            onClick={() => setMode("anonymous")}
            style={{
              background: "none",
              border: "none",
              color: "var(--muted, #6b7280)",
              fontSize: "0.82rem",
              cursor: "pointer",
              padding: 0,
              marginBottom: "12px",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <ChevronUp size={14} /> Skip — just send my request
          </button>

          <p style={{ margin: "0 0 6px", fontWeight: 700, fontSize: "1rem", color: "var(--text, #1C1C1E)" }}>
            Sign in to submit
          </p>
          <p style={{ margin: "0 0 16px", fontSize: "0.86rem", color: "var(--muted, #6b7280)", lineHeight: 1.5 }}>
            Using <strong style={{ color: "var(--text, #1C1C1E)" }}>{form.email}</strong>. Enter your password and we'll attach this request to your dashboard.
          </p>

          <form onSubmit={handleSignInAndSubmit}>
            <div style={{ marginBottom: "6px" }}>
              <label style={{ display: "block", fontWeight: 600, marginBottom: "6px", fontSize: "0.9rem" }}>Password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showSignInPassword ? "text" : "password"}
                  value={signInPassword}
                  onChange={(e) => setSignInPassword(e.target.value)}
                  placeholder="Your password"
                  style={{ width: "100%", fontSize: "1rem", padding: "14px 46px 14px 14px", borderRadius: "12px", border: "2px solid var(--border, #e5e7eb)", boxSizing: "border-box" }}
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowSignInPassword((v) => !v)}
                  style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--muted, #6b7280)", display: "flex", alignItems: "center" }}
                  tabIndex={-1}
                  aria-label={showSignInPassword ? "Hide password" : "Show password"}
                >
                  {showSignInPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <p style={{ margin: "10px 0 0", fontSize: "0.82rem", color: "var(--muted, #6b7280)" }}>
              Wrong account?{" "}
              <button
                type="button"
                onClick={() => setMode("signup")}
                style={{ background: "none", border: "none", padding: 0, color: "var(--primary)", fontWeight: 600, cursor: "pointer", textDecoration: "underline", fontSize: "inherit" }}
              >
                Create a new one
              </button>
            </p>

            <button
              type="submit"
              disabled={loading || submitting}
              style={{
                marginTop: "16px",
                background: "var(--primary)",
                color: "#fff",
                border: "none",
                borderRadius: "14px",
                padding: "16px 32px",
                fontSize: "1rem",
                fontWeight: 700,
                cursor: (loading || submitting) ? "not-allowed" : "pointer",
                width: "100%",
                opacity: (loading || submitting) ? 0.7 : 1,
              }}
            >
              {loading || submitting ? "Signing in & sending…" : "Sign in & send →"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
